import React, { useCallback, useMemo, useState } from 'react';

import {
    ListItem,
    Divider, Drawer, List,
    ListItemText, Collapse,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter, matchPath } from 'react-router-dom';
import urlJoin from 'url-join';

import Icon from '~/components/Icon';
import IconButton from '~/components/IconButton';
import Popover from '~/components/Popover';
import UserAvatar from '~/components/UserAvatar';
import { connectScreenSize } from '~/helpers/screen-size-helper';
import useClassNames from '~/hooks/use-classnames';
import useDidMount from '~/hooks/use-did-mount';
import useDidMountAndUpdate from '~/hooks/use-did-mount-and-update';
import { actions } from '~/redux/ducks';
import thunks from '~/redux/thunks';
import routes, { allRoutes } from '~/routes/app';
import { findParentGroups, hasRoutePermission } from '~/routes/manager';
import colors from '~/values/colors';

import styles from './AppSideMenu.module.scss';

/* eslint-disable import/no-cycle */

const transitionDurationDrawer = { enter: 100, exit: 300 };

const ItemIcon = ({
    icon,
    isActive,
}) => {
    const classes = useClassNames([
        styles.menuIcon,
        { [styles.activeMenuIcon]: isActive },
    ]);

    return (
        <Icon
            size={19}
            name={icon.name}
            className={classes}
            family={icon.family}
            color={colors.primaryDark}
        />
    );
};

const SubMenu = ({
    isOpen, spacement,
    route, renderItem,
}) => {
    const [isMenuOpened, setIsMenuOpened] = useState(isOpen);

    const handleClick = useCallback(() => {
        setIsMenuOpened(prevOpen => !prevOpen);
    }, []);

    const style = useMemo(() => {
        return { paddingLeft: `${spacement * 8}px` };
    }, [spacement]);

    return (
        <div>
            <ListItem
                key={route.group}
                style={style}
                id={route.group}
                onClick={handleClick}
                className={styles.subMenuItem}
            >
                {route.icon ? (
                    <ItemIcon icon={route.icon} />
                ) : null}
                <ListItemText primary={route.group} />
                <Icon
                    size={19}
                    className={styles.arrowIcon}
                    name={isMenuOpened ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                />
            </ListItem>
            <Collapse
                unmountOnExit
                timeout="auto"
                in={isMenuOpened}
                className={styles.subMenuCollapse}
            >
                <List component="div" disablePadding>
                    {route.routes.map(routeItem => {
                        return renderItem(routeItem, spacement + 2);
                    })}
                </List>
            </Collapse>
        </div>
    );
};

const MenuItem = ({
    isActive,
    spacement,
    route, onClick,
}) => {

    const to = useMemo(() => ({
        pathname: urlJoin('/app', route.path),
        search: route.query,
    }), [route]);

    const menuClasses = useClassNames([
        styles.menuItem,
        { [styles.activeMenuItem]: isActive },
    ]);

    const style = useMemo(() => {
        return { paddingLeft: `${spacement * 8}px` };
    }, [spacement]);

    return (
        <Link
            to={to}
            onClick={onClick}
            className={styles.link}
        >
            <ListItem
                style={style}
                id={route.menuKey}
                selected={isActive}
                className={menuClasses}
            >
                {route.icon ? (
                    <ItemIcon
                        icon={route.icon}
                        isActive={isActive}
                    />
                ) : null}
                <ListItemText>
                    <span>
                        {route.text}
                    </span>
                </ListItemText>
            </ListItem>
        </Link>
    );
};

const AppSideMenu = ({
    sidebarCollapsed,
    location, user, history,
    toggleSidebar, isDesktop,
    removeAuthenticationData,
    isMobile,
}) => {
    const [openedKeys, setOpenedKeys] = useState([]);

    const variantDrawer = !isDesktop || sidebarCollapsed ? 'temporary' : 'permanent';
    const drawerOpened = !sidebarCollapsed;

    const drawerClasses = useClassNames([
        styles.drawer,
        { [styles.permanentDrawer]: isDesktop },
    ]);

    useDidMount(() => {
        toggleSidebar(!isDesktop);
    });

    useDidMountAndUpdate(() => {
        if (sidebarCollapsed) {
            setOpenedKeys([]);
        }
    }, [sidebarCollapsed]);

    const handleLogout = useCallback(() => {
        removeAuthenticationData();
        history.push('/login');
    }, [history, removeAuthenticationData]);

    const handleCloseDrawer = useCallback(() => {
        toggleSidebar(true);
    }, [toggleSidebar]);

    const handleLinkClick = useCallback(() => {
        if (!isDesktop) {
            toggleSidebar(true);
        }
    }, [isDesktop, toggleSidebar]);

    const menuState = useMemo(() => {
        const { pathname } = location;

        const selectedRoute = allRoutes
            .find(route => {
                return matchPath(pathname, {
                    path: urlJoin('/app', route.path),
                    exact: route.exact,
                });
            });

        if (!selectedRoute) {
            return { selectedRoute: {}, openedGroups: [] };
        }

        const openedGroups = sidebarCollapsed
            ? []
            : findParentGroups(routes, selectedRoute).map(r => r.group);

        return {
            selectedRoute,
            openedGroups: [
                ...openedKeys,
                ...openedGroups.filter(item => (
                    openedKeys.indexOf(item) === -1
                )),
            ],
        };
    }, [location, openedKeys, sidebarCollapsed]);

    const renderMenuGroup = (route, spacement) => {
        const isOpen = menuState.openedGroups.find(group => group === route.group);

        return (
            <SubMenu
                key={route.group}
                route={route}
                isOpen={isOpen}
                spacement={spacement}
                // eslint-disable-next-line no-use-before-define
                renderItem={renderMenuItem}
            />
        );
    };

    const renderMenuItem = (route, spacement) => {
        if (!hasRoutePermission(route)) return null;
        if (route.group) return renderMenuGroup(route, spacement);
        if (!route.text) return null;

        const isActive = menuState.selectedRoute?.menuKey === route.menuKey;

        return (
            <MenuItem
                key={route.menuKey}
                route={route}
                isActive={isActive}
                spacement={spacement}
                onClick={handleLinkClick}
            />
        );
    };

    const renderUserOptions = () => {
        return (
            <List className={styles.userList}>
                <Link to="/app/perfil" className={styles.link}>
                    <ListItem className={styles.listItem}>
                        <Icon
                            size={19}
                            name="person"
                            className={styles.profileIcon}
                        />
                        <ListItemText>
                            Perfil
                        </ListItemText>
                    </ListItem>
                </Link>
                <ListItem onClick={handleLogout} className={styles.listItem}>
                    <Icon
                        size={20}
                        name="keyboard-arrow-left"
                        className={styles.leaveIcon}
                    />
                    <ListItemText>
                        <span className={styles.leaveText}>
                            Sair
                        </span>
                    </ListItemText>
                </ListItem>
            </List>
        );
    };

    const renderAvatar = () => {
        const { nome, email, foto: profilePicture } = user;

        return (
            <div>
                <div className={styles.sideMenuCloser}>
                    {isMobile && drawerOpened ? (
                        <IconButton
                            icon="close"
                            title="Fechar"
                            onClick={handleCloseDrawer}
                            className={styles.closeButton}
                        />
                    ) : null}
                </div>
                <div className={styles.avatarItem}>
                    <UserAvatar
                        size={48}
                        hidePopover
                        name={nome}
                        email={email}
                        className={styles.avatar}
                        photo={profilePicture && profilePicture.caminho}
                    />
                    <Popover
                        popoverId="user-popover"
                        popoverContent={renderUserOptions()}
                    >
                        <div className={styles.userInfo}>
                            <b>{nome}</b>
                            <div className={styles.userEmail}>
                                {email}
                                <Icon
                                    size={19}
                                    name="keyboard-arrow-down"
                                    className={styles.arrowIcon}
                                />
                            </div>
                        </div>
                    </Popover>
                </div>
            </div>
        );
    };

    return (
        <Drawer
            anchor="left"
            open={drawerOpened}
            variant={variantDrawer}
            className={drawerClasses}
            onClose={handleCloseDrawer}
            transitionDuration={transitionDurationDrawer}
        >
            <div className={styles.scrollContainer}>
                {renderAvatar()}
                <Divider />
                <List className={styles.menuList}>
                    {routes.map(route => renderMenuItem(route, 2))}
                </List>
            </div>
        </Drawer>
    );
};

AppSideMenu.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
    sidebarCollapsed: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    toggleSidebar: PropTypes.func.isRequired,
};

const mapStateToProps = ({ sidebarCollapsed, user }) => ({
    user,
    sidebarCollapsed,
});

const mapDispatchToProps = {
    toggleSidebar: actions.sidebar.toggleSidebar,
    removeAuthenticationData: thunks.user.removeAuthenticationData,
};

export default connectScreenSize(
    withRouter(
        connect(mapStateToProps, mapDispatchToProps)(AppSideMenu),
    ),
);
